import { i18nFn, useSpringBigStore } from '@springbig/shared';
import React, { useState } from 'react';
import { useEffectOnce } from 'usehooks-ts';
import { HStack, Input, VStack } from 'native-base';
import { Caption } from '~/shared/Typography';
import { useGeneralStyles } from '~/shared/styles';
import { RestrictionsDisclaimer } from '~/pages/giftCard/RestrictionsDisclaimer';

const PointsConverterField = ({
    t,
    onChange
}: {
    t: i18nFn,
    onChange: (value: number) => void;
}) => {

    const { member, merchant } = useSpringBigStore();
    const gStyle = useGeneralStyles();

    const maxPointAmountCorrectedByMaxDollarAmount = Math.floor(
        Math.min(
            Number(member?.remaining_dollar_amount_for_current_period) / 100,
            Number(merchant?.gc_dollar_amount_one_time)
        ) * Number(merchant.dollars_per_point)
    );

    const maxPointPerPeriodCorrectedByMaxDollarAmount = Math.floor(
        (Number(member?.remaining_dollar_amount_for_current_period) / 100)
         * Number(merchant.dollars_per_point)
    );

    const maxValue = (
        merchant?.gift_card_points_restrictions ?
            Math.min(
                member?.balance || 0,
                maxPointAmountCorrectedByMaxDollarAmount,
                merchant?.gc_point_amount_one_time || 0,
                member?.remaining_point_amount_for_current_period || 0
            )
            :
            Math.min(
                maxPointAmountCorrectedByMaxDollarAmount,
                member?.balance || 0
            )
    );

    const [convertPoints, setConvertPoints] = useState(maxValue);

    const handlePointsChange = (points: string): void => {

        const minValue = 0;

        let numericValue = points.replace(/[^0-9]/g, '');

        if (numericValue.length === 0 || numericValue === 'NaN') {
            numericValue = minValue.toString();
        }

        const finalAmount = Math.min(Math.max(parseInt(numericValue), minValue), maxValue);
        setConvertPoints(finalAmount);
    };

    const debouncePointsAmountNormalization = (): void => {

        // insures that the amount is divisible by the merchant's dollars_per_point value to avoid decimals
        let numericValue = convertPoints;

        if (numericValue % Number(merchant.dollars_per_point) !== 0) {
            numericValue = (Math.floor(numericValue / Number(merchant.dollars_per_point)) * Number(merchant.dollars_per_point));
        }

        setConvertPoints(numericValue);
        onChange(numericValue);
    };

    useEffectOnce(() => {

        // set initial point value to the highest value possible
        if (member && merchant) {
            debouncePointsAmountNormalization();
        }
    });

    return (
        <VStack>
            <Caption fontSize={18} fontWeight={'300'}>
                { t('general.points') }
            </Caption>
            <HStack space={4} justifyContent={'space-between'} alignItems={'baseline'}>
                <HStack space={4} alignItems={'baseline'}>
                    <Input
                        borderColor={gStyle.button_background}
                        placeholder={t('giftCard.pointsConvertor.form.accountNumber.placeholder')}
                        onChangeText={handlePointsChange}
                        onBlur={debouncePointsAmountNormalization}
                        value={convertPoints.toString()}
                        keyboardType="numeric"
                        borderWidth={1}
                        fontSize={18}
                        fontWeight={200}
                        paddingX={8}
                        borderRadius={7}
                        textAlign='center'
                        maxWidth={'100px'}
                        returnKeyType='done'
                    />
                    <Caption fontSize={18} fontWeight={'300'}>
                        { t('giftCard.points_for') }
                    </Caption>
                </HStack>
                <Caption fontSize={18} fontWeight={'300'} color={gStyle.button_background}>
                    ${ (convertPoints / parseFloat(merchant.dollars_per_point)).toFixed(2) }
                </Caption>
            </HStack>
            { member && merchant && merchant.gift_card_points_restrictions ?
                <RestrictionsDisclaimer t={t} type={'point'} maxPeriod={maxPointPerPeriodCorrectedByMaxDollarAmount} maxOneTime={maxValue} /> :
                <></>
            }
        </VStack>
    );
};

export default PointsConverterField;
